import React from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const faqs = [
  {
    question: 'Q: What does "Patent File Wrapper Downloader" do?',
    answer: "A: It downloads the entire prosecution history available at the USPTO for a given application.",
  },
  {
    question: "Q: What download options are available in the Patent File Wrapper Downloader?",
    answer: "A: The Patent File Wrapper Downloader provides two download options:",
    sections: [
      {
        title: "Collated File",
        bullets: [
          "Downloads the complete prosecution history as a single PDF.",
          "Includes bookmarks for easy navigation between prosecution events.",
          "Best suited for users who prefer reviewing the entire file wrapper in one document.",
        ],
      },
      {
        title: "Individual Files",
        bullets: [
          "Downloads each prosecution document as a separate file.",
          "Each file is automatically named for easy identification.",
          "Ideal for document management, sharing, and selective review.",
        ],
      },
    ],
  },
  {
    question: "Q: How many applications can I enter at once?",
    answer: "A: Up to 10 applications.",
  },
  {
    question: "Q: Can I search using a publication or patent number?",
    answer: "A: No. Only valid US application/serial numbers are accepted.",
  },
  {
    question: "Q: How are the individual files named?",
    answer: "A: Each file is automatically named using the following format:",
    formatLine: "Application Number – Mailing Date – Prosecution Event",
    formatSuffix: "This naming convention makes it easy to identify the document without opening it.",
  },
  {
    question: "Q: Which download option should I use?",
    answer: "A: It depends on how you plan to use the prosecution documents:",
    bullets: [
      { bold: "Collated File", text: " if you want to review the complete prosecution history in a single PDF." },
      { bold: "Individual Files", text: " if you need to organize, upload, share, or review specific prosecution documents independently." },
    ],
  },
  {
    question: "Q: Will all prosecution documents be included in the Individual Files download?",
    answer:
      "A: Yes. All available prosecution documents associated with the application are downloaded as separate files.",
  },
  {
    question: "Q: Will the contents of the downloaded documents change?",
    answer:
      "A: No. The download option changes only the output format. The document content remains unchanged.",
  },
  {
    question: "Q: Can I download both formats for the same application?",
    richAnswer: [
      { text: "A: Yes. You can download the prosecution documents in either format whenever required. However, you can select only one download option (" },
      { text: "Collated File", bold: true },
      { text: " or " },
      { text: "Individual Files", bold: true },
      { text: ") per request." },
    ],
  },
  {
    question: "Q: Why are Individual Files beneficial?",
    answer: "A: Downloading individual files helps users:",
    bullets: [
      "Locate specific prosecution documents more quickly.",
      "Organize files more efficiently.",
      "Upload documents into Document Management Systems (DMS) without manual splitting.",
      "Share only the required prosecution documents with colleagues or clients.",
    ],
  },
  {
    question: "Q: How can I find a specific document in the downloaded files?",
    richAnswer: [
      { text: "A: For " },
      { text: "Collated File", bold: true },
      { text: " downloads, use the bookmarks available in the output PDF. They show the document type and mailing date, allowing you to jump directly to any document. For " },
      { text: "Individual Files", bold: true },
      { text: ", each document is saved separately and named for easy identification." },
    ],
  },
];

const BulletList = ({ items }) => (
  <Box component="ul" sx={{ mt: 0, mb: 0, pl: 2.5 }}>
    {items.map((item, i) => (
      <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
        {typeof item === "string" ? item : (
          <><Box component="span" sx={{ fontWeight: 600 }}>{item.bold}</Box>{item.text}</>
        )}
      </Typography>
    ))}
  </Box>
);

BulletList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        bold: PropTypes.string,
        text: PropTypes.string,
      }),
    ])
  ),
};

const FaqAnswer = ({ faq }) => (
  <Box>
    {faq.richAnswer && (
      <Typography variant="body2">
        {faq.richAnswer.map((part, i) =>
          part.bold
            ? <Box key={i} component="span" sx={{ fontWeight: 600 }}>{part.text}</Box>
            : <React.Fragment key={i}>{part.text}</React.Fragment>
        )}
      </Typography>
    )}
    {faq.answer && (
      <Typography variant="body2" sx={{ whiteSpace: "pre-line", mb: faq.formatLine || faq.bullets || faq.sections ? 1 : 0 }}>
        {faq.answer}
      </Typography>
    )}
    {faq.formatLine && (
      <Box sx={{ my: 0.75 }}>
        <Typography variant="body2" fontWeight={600}>{faq.formatLine}</Typography>
        {faq.formatSuffix && (
          <Typography variant="body2" sx={{ mt: 0.75 }}>{faq.formatSuffix}</Typography>
        )}
      </Box>
    )}
    {faq.bullets && <BulletList items={faq.bullets} />}
    {faq.sections && faq.sections.map((section, i) => (
      <Box key={i} sx={{ mt: i === 0 ? 1 : 1.5 }}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          {section.title}
        </Typography>
        <BulletList items={section.bullets} />
      </Box>
    ))}
  </Box>
);

FaqAnswer.propTypes = {
  faq: PropTypes.shape({
    answer: PropTypes.string,
    richAnswer: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string,
        bold: PropTypes.bool,
      })
    ),
    formatLine: PropTypes.string,
    formatSuffix: PropTypes.string,
    bullets: PropTypes.array,
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        bullets: PropTypes.array,
      })
    ),
  }),
};

const PatentFileWrapperDownloader = () => (
  <Box>
    {faqs.map((faq, i) => (
      <Accordion
        key={i}
        defaultExpanded={i === 0}
        sx={{
          backgroundColor: "#fff",
          "&.Mui-expanded": { margin: 0 },
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="body2" fontWeight="bold">
            {faq.question}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FaqAnswer faq={faq} />
        </AccordionDetails>
      </Accordion>
    ))}
  </Box>
);

export default PatentFileWrapperDownloader;
